/**
 * What a progression operation answers with.
 *
 * The same shape `InventoryResult` uses, and for the same reason: spending a point is something a
 * screen does while the player watches, and a refusal that carries the numbers behind it needs no
 * second lookup to be turned into a sentence. Nothing here throws.
 */

import type { PerkId } from '@/sim/progression/perkContract';

export type ProgressionFailure =
  /** A string from a save that no longer names a perk. Ids are refused, never dropped. */
  | { readonly reason: 'unknownPerk'; readonly perkId: string }
  | { readonly reason: 'noPerkPoints'; readonly available: number }
  | { readonly reason: 'noStatPoints'; readonly available: number }
  | { readonly reason: 'atMaxRank'; readonly perkId: PerkId; readonly maxRanks: number }
  /** The stat is already at the ceiling the combat model allows. */
  | { readonly reason: 'atStatCap'; readonly statId: string; readonly cap: number }
  | { readonly reason: 'levelTooLow'; readonly perkId: PerkId; readonly minLevel: number }
  /** A prerequisite that is not yet at full rank. Half a requirement is not a requirement. */
  | {
      readonly reason: 'missingRequirement';
      readonly perkId: PerkId;
      readonly requires: PerkId;
    }
  /** A rank count from a save that is negative, fractional, or past what the perk offers. */
  | { readonly reason: 'invalidRank'; readonly perkId: PerkId; readonly rank: number };

export type ProgressionResult<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly failure: ProgressionFailure };

export function succeed<T>(value: T): ProgressionResult<T> {
  return { ok: true, value };
}

export function fail<T>(failure: ProgressionFailure): ProgressionResult<T> {
  return { ok: false, failure };
}
